/**
 * unitConversion.ts
 * -----------------------------------------------------------------------------
 * Conversion + normalization functions over the UNIT_CATEGORIES tables.
 *
 * Affine model: canonical = value * factor + offset
 *               value     = (canonical - offset) / factor
 *
 * Unknown-ness is handled at two levels, deliberately differently:
 *   - Unknown CATEGORY → pass-through (value returned as-is). Forward-compat:
 *     an org may author a category the runtime doesn't convert yet; storing
 *     beats erroring, and callers can gate on isSupportedCategory().
 *   - Unknown UNIT within a KNOWN category → throw. Silently storing an
 *     unconverted value into a converted category corrupts canonicalValue
 *     comparisons (the old behaviour let a 'mi' entry sit alongside 'km'
 *     entries at the wrong scale).
 */

import { UNIT_CATEGORIES, UnitDefinition, UnitCategoryDefinition } from './unitDefinitions';

/** Look up a category definition (undefined when not runtime-supported). */
export function getUnitCategory(category: string): UnitCategoryDefinition | undefined {
  return UNIT_CATEGORIES[category];
}

/** Whether the runtime can actually convert this category. */
export function isSupportedCategory(category: string): boolean {
  return category in UNIT_CATEGORIES;
}

/** The unit canonicalValue is stored in for a category (category itself when unsupported). */
export function getCanonicalUnit(category: string): string {
  return UNIT_CATEGORIES[category]?.canonicalUnit ?? category;
}

/**
 * Resolve a unit spelling to its canonical symbol within a category.
 * Match order: exact symbol → case-insensitive symbol → alias (lowercased/trimmed).
 * Unknown units (or unsupported categories) return the input unchanged so the
 * caller decides whether to fail — see resolveUnit/toCanonical.
 */
export function normalizeUnit(unit: string, category: string): string {
  const cat = UNIT_CATEGORIES[category];
  if (!cat || !unit) return unit;

  if (cat.units[unit]) return unit;

  const needle = unit.trim().toLowerCase();
  for (const [symbol, def] of Object.entries(cat.units)) {
    if (symbol.toLowerCase() === needle) return symbol;
    if (def.aliases?.includes(needle)) return symbol;
  }
  return unit;
}

/** Whether a unit spelling resolves to a known unit of the category. */
export function isKnownUnit(unit: string, category: string): boolean {
  const cat = UNIT_CATEGORIES[category];
  if (!cat) return false;
  return normalizeUnit(unit, category) in cat.units;
}

function resolveUnit(unit: string, category: string): UnitDefinition {
  const cat = UNIT_CATEGORIES[category];
  // Callers only reach here for supported categories.
  const symbol = normalizeUnit(unit, category);
  const def = cat?.units[symbol];
  if (!def) {
    const known = cat ? Object.keys(cat.units).join(', ') : '';
    throw new Error(`Unknown unit '${unit}' for category '${category}'. Known units: ${known}`);
  }
  return def;
}

/**
 * Convert a value to the category's canonical unit.
 * Unsupported category → value unchanged. Unknown unit in a supported category → throws.
 */
export function toCanonical(value: number, unit: string, category: string): number {
  if (!isSupportedCategory(category)) return value;
  const def = resolveUnit(unit, category);
  return value * def.factor + (def.offset ?? 0);
}

/**
 * Convert a canonical value to a target unit of the category.
 * Unsupported category → value unchanged. Unknown unit in a supported category → throws.
 */
export function fromCanonical(canonicalValue: number, targetUnit: string, category: string): number {
  if (!isSupportedCategory(category)) return canonicalValue;
  const def = resolveUnit(targetUnit, category);
  return (canonicalValue - (def.offset ?? 0)) / def.factor;
}

/** Convert a value between two units of the same category. */
export function convertBetweenUnits(
  value: number,
  fromUnit: string,
  toUnit: string,
  category: string
): number {
  return fromCanonical(toCanonical(value, fromUnit, category), toUnit, category);
}

/** Unit symbols of a category in table order (empty when unsupported). */
export function getCategoryUnits(category: string): string[] {
  const cat = UNIT_CATEGORIES[category];
  return cat ? Object.keys(cat.units) : [];
}

/** Full definition for one unit (undefined when unknown), post-normalization. */
export function getUnitDefinition(unit: string, category: string): UnitDefinition | undefined {
  const cat = UNIT_CATEGORIES[category];
  if (!cat) return undefined;
  return cat.units[normalizeUnit(unit, category)];
}
